import qs from 'qs'
import useSWRMutation from 'swr/mutation'
import { ThrowableApiError } from './errors'
import type { ThrowableApiErrorData } from './errors'
import type {
  ErrorCode,
  ErrorData,
  Method,
  StringHeaders,
} from '@captaincause/types'
import type { SWRMutationConfiguration } from 'swr/mutation'

type AnyObject = { [key: string]: any }

type FetcherArgs = [
  url: string,
  headers: StringHeaders | undefined,
  method: Method,
  asParams: boolean,
  credentials?: RequestCredentials,
  cache?: Request['cache'],
]

export type MutationTriggerArgs =
  | {
      _triggerHeaders?: StringHeaders
    }
  | undefined

function mergeHeaders(...args: (StringHeaders | undefined)[]): Headers {
  const flattened = (args.filter(headers => !!headers) as StringHeaders[]).flat(
    1,
  )
  if (flattened.length === 0) return new Headers()

  return new Headers(flattened)
}

export async function fetcher<Data, TriggerArgs>(
  [url, headers, method, asParams, credentials, cache]: FetcherArgs,
  { arg: args }: Readonly<{ arg: TriggerArgs & MutationTriggerArgs }>,
): Promise<Data> {
  const { _triggerHeaders, ...triggerArgs } = args ?? {}
  const hasArgs = !!Object.keys(triggerArgs).length

  const requestHeader = mergeHeaders(headers, _triggerHeaders)
  requestHeader.set('Content-Type', 'application/json')

  const body = !asParams && hasArgs ? JSON.stringify(triggerArgs) : undefined

  const urlParameters =
    asParams && hasArgs
      ? qs.stringify(triggerArgs, {
          encode: false,
          arrayFormat: 'comma',
          addQueryPrefix: true,
        })
      : ''

  const requestUrl = `${url}${urlParameters}`

  const [response] = await Promise.all([
    fetch(requestUrl, {
      method,
      headers: requestHeader,
      credentials,
      body,
      cache,
    }),
  ])

  if (!response.ok) {
    const error: ErrorData | ThrowableApiErrorData = await response.json()
    if (error && 'message' in error && 'status' in error) {
      throw ThrowableApiError({
        message: 'useMutation: error while mutating',
        status: error.status,
        data: error.data,
      })
    }
    const errorData = error as ErrorData

    throw ThrowableApiError({
      message: 'useMutation: error while mutating',
      status: response.status,
      data: {
        error: errorData?.error || '',
        code:
          errorData?.code || (response.status.toString() as ErrorCode) || '400',
      },
    })
  }

  return (await response.json()) as Data
}

interface UseMutationArgs<Data, TriggerArgs> {
  asParams?: boolean
  credentials?: RequestCredentials
  headers?: StringHeaders
  method?: Method
  url: string
  cache?: Request['cache']
  swrConfig?: Partial<
    SWRMutationConfiguration<
      Data,
      ThrowableApiErrorData,
      TriggerArgs & MutationTriggerArgs,
      FetcherArgs
    >
  >
}

export function useMutation<
  Data extends AnyObject = never,
  TriggerArgs = MutationTriggerArgs,
>({
  url,
  headers = undefined,
  method = 'POST',
  asParams = false,
  credentials = undefined,
  swrConfig = undefined,
  cache = 'default',
}: UseMutationArgs<Data, TriggerArgs>) {
  return useSWRMutation<
    Data,
    ThrowableApiErrorData,
    FetcherArgs,
    TriggerArgs & MutationTriggerArgs
  >([url, headers, method, asParams, credentials, cache], fetcher, swrConfig)
}
